// Adds folders and files to a Drive sync, browsed or searched from inside its connected
// shared drive. Shared by the project Drive Sync card and the Global data page.
import { useEffect, useRef, useState } from "react";

import {
  DriveBrowseItem,
  DriveBrowseSource,
  DriveCoverage,
  DriveSearchType,
  useDriveBrowse,
} from "../api/hooks/useDriveSync";
import formStyles from "./FormControls.module.css";
import styles from "./DriveSyncSettingsCard.module.css";

// Mirrors MAX_ADDED_PER_REQUEST on both add routes (folders and files together).
const MAX_ADDED_PER_REQUEST = 10;

const COVERAGE_LABEL: Record<NonNullable<DriveCoverage>, string> = {
  root: "Synced",
  tracked: "Tracked",
  under_root: "Inside a synced folder",
  excluded: "Excluded",
};

const ALREADY_COVERED: ReadonlySet<DriveCoverage> = new Set(["root", "tracked", "under_root"]);

const SEARCH_TYPE_ORDER: DriveSearchType[] = ["folder_name", "file_name", "file_content"];

const SEARCH_TYPES: Record<DriveSearchType, { label: string; placeholder: string }> = {
  folder_name: { label: "Folder name", placeholder: "Words in the folder's name" },
  file_name: { label: "File name", placeholder: "Words in the file's name" },
  file_content: { label: "File content", placeholder: "Words in the file" },
};

interface Crumb {
  folderId: string;
  name: string;
}

export interface DriveAddSelection {
  folderIds: string[];
  fileIds: string[];
}

interface Props {
  open: boolean;
  driveName: string;
  // Where added files are read into, e.g. "the project".
  destination: string;
  source: DriveBrowseSource;
  adding: boolean;
  // A folder at the drive's root to open in, by name; null opens at the root. The
  // root stays one click away in the breadcrumb, and a missing folder opens there.
  startInFolder: string | null;
  onAdd: (selection: DriveAddSelection) => Promise<void>;
  onClose: () => void;
}

export const DriveAddSourcesDialog = ({
  open,
  driveName,
  destination,
  source,
  adding,
  startInFolder,
  onAdd,
  onClose,
}: Props) => {
  const ref = useRef<HTMLDialogElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [trail, setTrail] = useState<Crumb[]>([]);
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [searchType, setSearchType] = useState<DriveSearchType>("folder_name");
  const [selected, setSelected] = useState<Map<string, DriveBrowseItem>>(new Map());
  const [error, setError] = useState<string | null>(null);
  // Set while the root listing is read to find ``startInFolder``, so the root never
  // flashes up before the dialog moves into the folder.
  const [findingStart, setFindingStart] = useState(false);

  const driveLabel = driveName || "Shared drive";
  const folderId = query ? null : (trail[trail.length - 1]?.folderId ?? null);
  const listing = useDriveBrowse(
    source,
    { folderId, query, searchType: query ? searchType : "folder_name" },
    open,
  );

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setTrail([]);
      setQueryInput("");
      setQuery("");
      setSearchType("folder_name");
      setSelected(new Map());
      setError(null);
      setFindingStart(startInFolder !== null);
      dialog.showModal();
      // Opening focuses the first control, the search-type select; typing is the
      // likelier first move.
      searchRef.current?.focus();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open, startInFolder]);

  useEffect(() => {
    if (!findingStart || folderId !== null || query) return;
    if (listing.isError) {
      setFindingStart(false);
      return;
    }
    if (!listing.data) return;
    const start = listing.data.items.find((i) => i.is_folder && i.name === startInFolder);
    if (start) setTrail([{ folderId: start.file_id, name: start.name }]);
    setFindingStart(false);
  }, [findingStart, folderId, query, listing.data, listing.isError, startInFolder]);

  const handleAddSelected = async () => {
    setError(null);
    const items = Array.from(selected.values());
    if (items.length > MAX_ADDED_PER_REQUEST) {
      setError(`Add at most ${MAX_ADDED_PER_REQUEST} folders and files at a time.`);
      return;
    }
    try {
      await onAdd({
        folderIds: items.filter((i) => i.is_folder).map((i) => i.file_id),
        fileIds: items.filter((i) => !i.is_folder).map((i) => i.file_id),
      });
    } catch (e) {
      setError((e as Error).message || "Adding to the Drive sync failed.");
    }
  };

  const toggleSelected = (item: DriveBrowseItem) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.file_id)) next.delete(item.file_id);
      else next.set(item.file_id, item);
      return next;
    });
  };

  const openFolder = (item: DriveBrowseItem) => {
    const crumb = { folderId: item.file_id, name: item.name };
    setTrail((prev) => (query ? [crumb] : [...prev, crumb]));
    setQuery("");
    setQueryInput("");
  };

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div className={styles.dialogBody}>
        <h3 className={styles.title}>Add folders or files</h3>
        <p className={styles.helpText}>
          Add folders and files from <strong>{driveLabel}</strong>. Anything already synced is
          greyed out; subfolders of a synced folder are chosen in that folder&apos;s tree
          instead. A folder you add syncs everything inside it; a file you add is tracked on its
          own. Every file is downloaded and read into {destination}, so add only what is
          relevant.
        </p>

        <form
          className={styles.searchRow}
          onSubmit={(e) => {
            e.preventDefault();
            setQuery(queryInput.trim());
          }}
        >
          <select
            className={`${formStyles.select} ${styles.searchTypeSelect}`}
            value={searchType}
            onChange={(e) => setSearchType(e.target.value as DriveSearchType)}
            aria-label="Search by"
          >
            {SEARCH_TYPE_ORDER.map((type) => (
              <option key={type} value={type}>
                {SEARCH_TYPES[type].label}
              </option>
            ))}
          </select>
          <input
            type="search"
            ref={searchRef}
            className={styles.input}
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder={SEARCH_TYPES[searchType].placeholder}
          />
          <button type="submit" className={styles.secondaryButton} disabled={!queryInput.trim()}>
            Search
          </button>
        </form>

        <div className={styles.crumbs}>
          {query ? (
            <>
              {SEARCH_TYPES[searchType].label} matching <strong>&ldquo;{query}&rdquo;</strong>
              <button type="button" className={styles.linkButton} onClick={() => setQuery("")}>
                Back to browsing
              </button>
            </>
          ) : (
            <>
              {trail.length === 0 ? (
                <span>{driveLabel}</span>
              ) : (
                <button type="button" className={styles.linkButton} onClick={() => setTrail([])}>
                  {driveLabel}
                </button>
              )}
              {trail.map((crumb, i) => (
                <span key={crumb.folderId}>
                  {" / "}
                  {i === trail.length - 1 ? (
                    crumb.name
                  ) : (
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => setTrail(trail.slice(0, i + 1))}
                    >
                      {crumb.name}
                    </button>
                  )}
                </span>
              ))}
            </>
          )}
        </div>

        <div className={styles.browseList}>
          {(listing.isLoading || findingStart) && (
            <p className={styles.muted}>Loading from Drive…</p>
          )}
          {listing.isError && (
            <p className={styles.error}>
              {(listing.error as Error)?.message || "Couldn't load from Drive."}{" "}
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => void listing.refetch()}
              >
                Retry
              </button>
            </p>
          )}
          {!findingStart && listing.data && listing.data.items.length === 0 && (
            <p className={styles.muted}>
              {query
                ? "Nothing matches."
                : trail.length === 0
                  ? `${driveLabel} is empty.`
                  : "This folder is empty."}
            </p>
          )}
          {!findingStart && listing.data?.items.map((item) => {
            const covered = ALREADY_COVERED.has(item.coverage);
            return (
              <div
                key={item.file_id}
                className={covered ? `${styles.browseRow} ${styles.browseRowCovered}` : styles.browseRow}
              >
                <input
                  type="checkbox"
                  checked={covered || selected.has(item.file_id)}
                  disabled={covered}
                  onChange={() => toggleSelected(item)}
                  aria-label={`Select ${item.name}`}
                />
                <span className={styles.kindLabel}>{item.is_folder ? "Folder" : "File"}</span>
                <span className={styles.browseName}>
                  {item.is_folder ? (
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => openFolder(item)}
                    >
                      {item.name}
                    </button>
                  ) : (
                    item.name
                  )}
                  {query && item.path !== item.name && (
                    <span className={styles.browsePath}>{item.path}</span>
                  )}
                </span>
                {item.coverage && (
                  <span
                    className={item.coverage === "excluded" ? styles.badgeExcluded : styles.badge}
                  >
                    {COVERAGE_LABEL[item.coverage]}
                  </span>
                )}
              </div>
            );
          })}
          {listing.data?.capped && (
            <p className={styles.helpText}>Showing the first matches only - narrow the search.</p>
          )}
          {listing.data && listing.data.skipped.length > 0 && (
            <p className={styles.helpText}>Not searched: {listing.data.skipped.join("; ")}</p>
          )}
        </div>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={styles.dialogFooter}>
          <span className={styles.footerSpacer} />
          <span className={styles.muted}>{selected.size} selected</span>
          <button type="button" className={styles.secondaryButton} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => void handleAddSelected()}
            disabled={selected.size === 0 || adding}
          >
            {adding ? "Adding…" : "Add selected"}
          </button>
        </div>
      </div>
    </dialog>
  );
};
